'use client'

import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import Chart from "../../components/chart";
import { IGoalContextData, GoalScreenContext } from "../../components/goals/context";
import { emptyGoal } from "../../state/reducer";
import Icon from '../../components/Icon';
import { faPlus as add } from '@fortawesome/free-solid-svg-icons';
import { useScreenSize } from "../../contexts/screenSizeContext";
import { LifePart } from "../../state/LifePart";
import GoalWidget from "../../components/goalWidget";
import { useData } from "../../storage/accessors";
import { InstructionModal } from "../../components/instructionModal";
import RolesList from "../../components/rolesList";

const INSTRUCTION_SEEN_KEY = 'goals_instruction_seen';

export default function GoalsPage() {
    const { roles, goals } = useData();
    const [showInstruction, setShowInstruction] = useState(false);
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const rolesList = Object.values(roles.objects).sort((a, b) => (a.order || 0) - (b.order || 0));
    const hasPoints = rolesList.length > 0 && rolesList.filter((p) => p.startpoint != 1).length > 0;
    const [goalScreenContext, setGoalScreenContext] = useState<IGoalContextData>({
        currentPart: null
    });

    const lifepart = goalScreenContext.currentPart;
    const filteredGoals = Object.values(goals.objects)
        .filter(v => lifepart && v.lifepart === lifepart.uuid)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    const maxOrder = filteredGoals.length > 0
        ? Math.max(...filteredGoals.map(g => g.order || 0))
        : 0;

    const updateContext = useCallback((update: Partial<IGoalContextData>) => {
        setGoalScreenContext(old => { return { ...old, ...update } })
    }, []);

    const moveGoalUp = useCallback((uuid: string) => {
        const currentGoal = filteredGoals.find(g => g.uuid === uuid);
        if (!currentGoal || !currentGoal) return;

        const currentIndex = filteredGoals.findIndex(g => g.uuid === uuid);
        if (currentIndex > 0) {
            const currentOrder = currentGoal.order;
            const prevOrder = filteredGoals[currentIndex - 1].order;
            goals.save(currentGoal.uuid, { order: prevOrder });
            goals.save(filteredGoals[currentIndex - 1].uuid, { order: currentOrder });
        }
    }, [filteredGoals]);

    const moveGoalDown = useCallback((uuid: string) => {
        const currentGoal = filteredGoals.find(g => g.uuid === uuid);
        if (!currentGoal || !currentGoal) return;

        const currentIndex = filteredGoals.findIndex(g => g.uuid === uuid);
        if (currentIndex < filteredGoals.length - 1) {
            const currentOrder = currentGoal.order;
            const nextOrder = filteredGoals[currentIndex + 1].order;
            goals.save(currentGoal.uuid, { order: nextOrder });
            goals.save(filteredGoals[currentIndex + 1].uuid, { order: currentOrder });
        }
    }, [filteredGoals]);

    const updatePart = useCallback((data: Partial<LifePart>) => {
        if (!lifepart)
            return;
        roles.save(lifepart.uuid, data);
        updateContext({ currentPart: { ...lifepart, ...data } });
    }, [lifepart, updateContext]);

    useEffect(() => {
        if (goalScreenContext.currentPart === null && rolesList.length > 0) {
            updateContext({ currentPart: rolesList[0] });
        }
    }, [goalScreenContext.currentPart, updateContext, roles]);

    useEffect(() => {
        const seen = localStorage.getItem(INSTRUCTION_SEEN_KEY);
        if (!seen) {
            setShowInstruction(true);
        }
    }, []);

    const handleInstructionClose = () => {
        setShowInstruction(false);
        localStorage.setItem(INSTRUCTION_SEEN_KEY, 'true');
    };

    return (
        <GoalScreenContext.Provider value={{ ...goalScreenContext, update: updateContext }}>
            {isMobile ? (
                <>
                    <Col xs={12} className="mb-3 text-center">
                        <h3>Мои цели</h3>
                    </Col>
                    <Col xs={12} className="mb-3">
                        <Chart
                            allMaxValues={!Boolean(hasPoints)}
                            showLabels={false}
                            selectedRole={goalScreenContext.currentPart || null}
                            onClick={(index) => updateContext({ currentPart: rolesList[index] })}
                            roles={rolesList}
                        />
                        <div className="mt-3">
                            <RolesList
                                rolesList={rolesList}
                                selectedRole={goalScreenContext.currentPart}
                                onClick={(role) => updateContext({ currentPart: role })}
                                isMobile={true}
                            />
                        </div>
                    </Col>
                    <Col xs={12}>
                        {lifepart && (
                            <div className="" style={{ top: '60px' }}>
                                <h4 className="sticky-top mb-3 bg-white z-3 py-2 border-bottom">Цели для &quot;{lifepart.name}&quot;</h4>
                                {filteredGoals.map((goal, index) => (
                                    <GoalWidget
                                        key={goal.uuid}
                                        goal={goal}
                                        index={index + 1}
                                        roleName={lifepart.name}
                                        onMoveUp={moveGoalUp}
                                        onMoveDown={moveGoalDown}
                                        isFirst={index === 0}
                                        isLast={index === filteredGoals.length - 1}
                                    />
                                ))}
                                {filteredGoals.length > 3 && <Alert variant="warning"><strong>Не рекомендуется ставить более 3 целей</strong></Alert>}
                                <Button variant="outline-success" className="w-100 mt-3" onClick={() => {
                                    const newGoal = emptyGoal(lifepart, maxOrder + 1);
                                    goals.create(newGoal);
                                }}>
                                    <Icon icon={add} className="me-2" />Записать цель
                                </Button>
                            </div>
                        )}
                    </Col>
                </>
            ) : (
                <Row className="d-flex">
                    <Col lg={5}>
                        <Chart
                            allMaxValues={!Boolean(hasPoints)}
                            showLabels={false}
                            selectedRole={goalScreenContext.currentPart || null}
                            onClick={(index) => updateContext({ currentPart: rolesList[index] })}
                            roles={rolesList}
                        />

                    </Col>
                    <Col lg={7}>
                        <h3 className="mb-3">Цели</h3>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <RolesList
                                rolesList={rolesList}
                                selectedRole={goalScreenContext.currentPart}
                                onClick={(role) => updateContext({ currentPart: role })}
                                isMobile={false}
                            />
                        </div>

                        {
                            lifepart && <>
                                <Form.Group >
                                    <h3 className="mb-3">Цели</h3>
                                    {filteredGoals.length === 0 && <Alert variant="info">Вы пока не записали цели "{lifepart.name}"</Alert>}
                                    {filteredGoals.map((goal, index) => (
                                        <GoalWidget
                                            key={goal.uuid}
                                            goal={goal}
                                            index={index + 1}
                                            roleName={lifepart?.name}
                                            onMoveUp={moveGoalUp}
                                            onMoveDown={moveGoalDown}
                                            isFirst={index === 0}
                                            isLast={index === filteredGoals.length - 1}
                                        />
                                    ))}
                                    {filteredGoals.length > 3 && <Alert variant="warning"><strong>Не рекомендуется ставить более 3 целей</strong></Alert>}
                                    <Row className="mt-3">
                                        <Col>
                                            <Button variant="outline-success" size="sm" onClick={() => {
                                                const newGoal = emptyGoal(lifepart, maxOrder + 1);
                                                goals.create(newGoal);
                                            }}>
                                                <Icon icon={add}></Icon> Записать цель
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </>
                        }

                    </Col >
                </Row >
            )
            }
            <InstructionModal show={showInstruction} onHide={handleInstructionClose} />
        </GoalScreenContext.Provider >
    );
}